import {Injectable} from '@angular/core';
import {DocumentModel} from '../models/document.model';
import {HttpClient, HttpEventType} from '@angular/common/http';
import {ConfigurationService} from './configuration.service';
import {UrlEndpoints} from '../definitions/url-endpoints';
import {ModalController, Platform} from '@ionic/angular';
import {PhotoDTOModel} from '../models/photoDTO.model';
import {GeneralService} from './general.service';
import {Subject} from 'rxjs';
import {CleanUpDocumentBeanService} from './clean-up-document-bean.service';
import {QueueStatus} from '../definitions/queue-status';
import {StateManagerService} from './state-manager.service';
import {CollectionsNames} from '../definitions/collections-names';
import {AppVersion} from '@ionic-native/app-version/ngx';

@Injectable({
    providedIn: 'root'
})
export class SyncDocumentsService {

    public progressMsg = 'Starting upload...';
    public loadingMsgSubject = new Subject<string>();
    public uploadStatus = new Subject<number>();

    constructor(private modalController: ModalController,
                private http: HttpClient,
                private generalServices: GeneralService,
                private configuration: ConfigurationService,
                private urlEndPoints: UrlEndpoints,
                private cleanUpDoc: CleanUpDocumentBeanService,
                private queueStatus: QueueStatus,
                private stateManager: StateManagerService,
                private collectionNames: CollectionsNames,
                private appVersion: AppVersion,
                private platform: Platform) {
    }

    async syncPics(doc: DocumentModel) {
        let index = 1;
        if (doc.photosURL && doc.photosURL.length > 0) {
            for (const imgEntry of doc.photosURL) {
                if (imgEntry.photoBase64) {
                    console.log(imgEntry);
                    console.log('Uploading picture: ', index);
                    await this.uploadPic(imgEntry, index, doc.photosURL.length);
                    index++;
                }
            }
        }
    }

    async uploadPic(imgEntry: PhotoDTOModel, index: number, nPhotos: number) {
        return new Promise((resolve, reject) => {
            this.http.post(this.configuration.buildUrl(this.urlEndPoints.photoBase64PostEndPoint), imgEntry, {
                reportProgress: true,
                observe: 'events'
            }).subscribe(event => {
                console.log(event);
                if (event.type === HttpEventType.UploadProgress) {
                    this.progressMsg = 'Uploading photo ' + index + ' of ' + nPhotos + '\n'
                        + Math.round(event.loaded / event.total * 100) + '%';
                    console.log(event.loaded / event.total);
                    this.uploadStatus.next(event.loaded / event.total);
                    this.loadingMsgSubject.next(this.progressMsg);
                } else if (event.type === HttpEventType.Response) {
                    console.log(event);
                    if (event.status === 200) {
                        imgEntry.uploaded = true;
                        imgEntry.photoBase64 = null;
                        console.log('Uploaded picture: ', index);
                        resolve(true);
                    }
                    if (event.status !== 200) {
                        reject(false);
                    }
                }
            }, error => {
                this.generalServices.createToast('Upload Failed');
                console.log(error);
                reject(false);
            });
        });
    }

    async syncDocuments(doc: DocumentModel) {
        let version = '';
        if ((this.platform.is('android') || this.platform.is('ios')) && !(this.platform.is('mobileweb'))) {
            version = await this.appVersion.getVersionNumber();
        }
        const tempDoc = JSON.parse(JSON.stringify(doc)) as DocumentModel;
        await this.cleanUpDoc.cleanDoc(tempDoc);
        tempDoc.selectedOperator = '' + this.configuration.getConfiguration().selectedOperator;
        tempDoc.deviceId = '' + this.configuration.getConfiguration().selectedOperator;
        tempDoc.mobileAppVersion = version;
        const mobileConfig = await this.configuration.getMobileConfiguration();
        let endpoint = '';
        console.log(mobileConfig);
        mobileConfig.mobilehireUseDocumentBeanQueue = 1;
        if (mobileConfig && mobileConfig.mobilehireUseDocumentBeanQueue === 1) {
            endpoint = this.urlEndPoints.documentBeanQueuePostEndPoint;
        } else {
            endpoint = this.urlEndPoints.documentPostEndPoint;
        }
        console.log(endpoint);
        return new Promise((resolve, reject) => {
            this.http.post(this.configuration.buildUrl(endpoint), tempDoc, {
                reportProgress: true,
                observe: 'events'
            }).subscribe((event) => {
                if (event.type === HttpEventType.UploadProgress) {
                    const progressValue = Math.round(event.loaded / event.total * 100);
                    if (progressValue < 100) {
                        this.progressMsg = 'Uploading document... ' + progressValue + '%';
                    } else {
                        this.progressMsg = 'Document already uploaded, awaiting server response';
                    }
                    this.loadingMsgSubject.next(this.progressMsg);
                    this.uploadStatus.next(event.loaded / event.total);
                } else if (event.type === HttpEventType.Response) {
                    if (event.status === 200) {
                        if (mobileConfig && mobileConfig.mobilehireUseDocumentBeanQueue === 1) {
                            // @ts-ignore
                            doc.queueId = +event.body.result;
                            doc.queueStatus = this.queueStatus.PROCESSING;
                            this.stateManager.upsert('document', '$loki', doc);
                        } else {
                            this.stateManager.findAndRemove('document', doc.$loki);
                        }

                        // what is this ??? synced documents should always be removed from document collection.
                        //if (doc.documentType === 8) {
                            this.removeQueuedDocument(doc);
                        //}

                        
                        if (doc.documentType === 1 && doc.parentDocument) {
                            this.removeQueuedDocument(doc.parentDocument);
                        }


                        this.generalServices.createToast('Document uploaded');
                        resolve(event.body);
                    }
                    if (event.status !== 200) {
                        reject(false);
                    }
                    console.log(event);
                }
            }, error => {
                this.generalServices.createToast('Upload Failed');
                console.log(error);
                reject(error);
            });
        });
    }

    async queryDocumentStatus() {
        const queryArray = [];
        let documentsPending = this.stateManager.getCollection('document') as DocumentModel[];
        documentsPending = documentsPending.filter(doc => {
            return doc.queueId && doc.queueId >= 0;
        });
        if (documentsPending && documentsPending.length > 0) {
            for (const doc of documentsPending) {
                queryArray.push(doc.queueId);
            }
            return new Promise<any>((resolve, reject) => {
                this.http.post(this.configuration.buildUrl(this.urlEndPoints.documentBeanQueueCheckQueueStatusEndPoint), queryArray, {
                    reportProgress: true,
                    observe: 'events'
                }).subscribe((event) => {
                    if (event.type === HttpEventType.Response) {
                        if (event.status === 200) {
                            console.log(event);
                            resolve(event);
                        }
                        if (event.status !== 200) {
                            reject(false);
                        }
                    }
                }, error => {
                    console.log(error);
                    reject(error);
                });
            });
        }
    }

    async checkDocumentBeanQueueStatus() {
        let documentsPending = this.stateManager.getCollection('document') as DocumentModel[];
        documentsPending = documentsPending.filter(doc => {
            return doc.queueId && doc.queueId >= 0;
        });
        console.log(documentsPending);
        if (documentsPending.length > 0) {
            this.queryDocumentStatus().then(res => {
                console.log(res.body);
                documentsPending.forEach(doc => {
                    const tempStatus = res.body.find(stat => {
                        return stat.id === +doc.queueId;
                    });
                    console.log(tempStatus);
                    if (tempStatus) {
                        doc.queueStatus = tempStatus.status;
                        console.log(doc);
                        if (doc.queueStatus === this.queueStatus.SUCCESS) {
                            this.stateManager.findAndRemove('document', doc.$loki);
                        }
                    }
                });
            }).catch(err => {
                console.log(err);
            });
        }
    }

    private removeQueuedDocument(doc: DocumentModel) {
        const queuedDocuments = this.stateManager.getCollection(this.collectionNames.QUEUEDDOCUMENTS) as DocumentModel[];
        queuedDocuments.forEach(eachDoc => {
            if (eachDoc.documentNumber === doc.documentNumber) {
                this.stateManager.findAndRemove(this.collectionNames.QUEUEDDOCUMENTS, eachDoc.$loki);
            }
        });
        this.stateManager.saveDatabase();
    }
}
